/* eslint-disable tailwindcss/no-arbitrary-value */
import React, { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { extend } from '@thd-nucleus/data-sources';
import { ProductDetails as ProductDetailsOG } from '../ProductDetails.component';
import { PIPSharebelt } from './PIPSharebelt';
import { useAccordionDeepLink } from './useAccordionDeepLink';
import { ACCORDION_SECTION_IDS } from '../../constants';

const STICKY_HEADER_HEIGHT = 125;
const STICKY_NAV_ID = 'sticky-nav';

export const PIPProductDetails = ({
  itemId, configId = undefined, hideRatingsIfHDPPSku = false, qaIndex = undefined,
  ratingIndex = undefined, showShareBeltPrint = false, showShareBeltShareButton = false, showShareBeltLabels = false,
  hideAddToList = false, showCollection = false, noPipSpacing = false, ...props
}) => {
  const qaSelector = `#${ACCORDION_SECTION_IDS.qa}`;
  const setExpandedRef = useRef();
  const ratingsRef = useRef();
  const qaRef = useRef();

  const getStickyHeaderOffset = () => {
    const stickyNav = document.getElementById(STICKY_NAV_ID);
    return stickyNav ? stickyNav.getBoundingClientRect().height : STICKY_HEADER_HEIGHT;
  };

  // Opens and scrolls to whichever section the URL asks for; the sections live elsewhere
  // on the page, so this talks to them over the life cycle event bus.
  useAccordionDeepLink({ getOffset: getStickyHeaderOffset });

  const handleScrolling = (element) => {
    if (typeof window === 'undefined' || !element) {
      return;
    }

    const top = element.getBoundingClientRect().top + window.scrollY - getStickyHeaderOffset();
    window.scroll({
      top,
      behavior: 'smooth'
    });
  };

  const onClick = useCallback((evt, target, index) => {
    if (typeof window !== 'undefined' && window.LIFE_CYCLE_EVENT_BUS) {
      window.LIFE_CYCLE_EVENT_BUS.trigger('product-details.ratings-click');
    }

    const isQaTarget = target === qaSelector;
    const targetIndex = isQaTarget ? qaIndex : (ratingIndex || index);

    if (setExpandedRef.current) {
      setExpandedRef.current(targetIndex)(evt, true);
    }

    handleScrolling(isQaTarget ? qaRef.current : ratingsRef.current);
  }, [ratingIndex, qaIndex]);

  useEffect(() => {
    if (typeof window !== 'undefined' && window?.LIFE_CYCLE_EVENT_BUS?.lifeCycle) {
      window.LIFE_CYCLE_EVENT_BUS.lifeCycle.on('accordion.mount', ({ output }) => {
        const { wrapperIds, setExpanded } = output;
        setExpandedRef.current = setExpanded;
        ratingsRef.current = document.querySelector('#' + wrapperIds[ratingIndex]);
        qaRef.current = document.querySelector('#' + wrapperIds[qaIndex]);
      });
    }
  }, []);

  return (
    <ProductDetailsOG itemId={itemId} configId={configId}>
      <ProductDetailsOG.Badge itemId={itemId} configId={configId} noPipSpacing={noPipSpacing} />
      <ProductDetailsOG.BrandCollection
        showCollection={showCollection}
        showBrandHyperLink
        hideRatingsIfHDPPSku={hideRatingsIfHDPPSku}
        noPipSpacing={noPipSpacing}
      />
      <div className="sui-w-[85%]">
        <ProductDetailsOG.Title />
      </div>
      {/* change logic to show when new media gallery gets apply */}
      <PIPSharebelt
        itemId={itemId}
        showShareBeltPrint={showShareBeltPrint}
        showShareBeltShareButton={showShareBeltShareButton}
        hideAddToList={hideAddToList}
        showShareBeltLabels={showShareBeltLabels}
      />
      <ProductDetailsOG.Ratings
        showBadge
        offsetselector={`#${STICKY_NAV_ID}`}
        qaselector={qaSelector}
        onClick={onClick}
        disableDeepLink
        useHeaderIndex={!!(ratingIndex || qaIndex)}
        hideRatingsIfHDPPSku={hideRatingsIfHDPPSku}
      />
    </ProductDetailsOG>
  );
};

PIPProductDetails.propTypes = {
  itemId: PropTypes.string.isRequired,
  hideRatingsIfHDPPSku: PropTypes.bool,
  configId: PropTypes.string,
  ratingIndex: PropTypes.number,
  qaIndex: PropTypes.number,
  showShareBeltPrint: PropTypes.bool,
  showShareBeltShareButton: PropTypes.bool,
  hideAddToList: PropTypes.bool,
  showShareBeltLabels: PropTypes.bool,
  noPipSpacing: PropTypes.bool,
  showCollection: PropTypes.bool
};

PIPProductDetails.displayName = 'PIPProductDetails';

PIPProductDetails.dataModel = extend({}, ProductDetailsOG);
